import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress
} from '@material-ui/core';
import BaseDialog from 'core/components/base-dialog/base-dialog';
import { Label } from 'core/components/label';
import { Card } from 'core/components/card';
import { Button } from 'core/components/button';
import { useToast } from 'core/components/toast';
import { DropzoneContainer } from 'core/components/dropzone';
import openFileIcon from 'core/assets/svg/open-file.svg';
import deleteIcon from 'core/assets/svg/delete-icon.svg';

import { getTaskDisplayName, appcuesModalIdsByTaskName } from '../../helpers/task-helpers';
import useStyles from './task-document-modal.styles';

const TaskDocumentModal = ({
  open,
  onClose,
  taskName,
  onUpload,
  uploading,
  existingFiles = [],
  onDownload,
  onDelete
}) => {
  const classes = useStyles();
  const toast = useToast();
  const [error, setError] = useState(null);
  const [uploadingFileName, setUploadingFileName] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deletingFileUrl, setDeletingFileUrl] = useState(null);
  const appcuesModalId = appcuesModalIdsByTaskName[taskName];
  const hasExistingFiles = existingFiles && existingFiles.length > 0;

  const handleLearnMore = () => {
    if (appcuesModalId && window?.Appcues) {
      window.Appcues.show(appcuesModalId);
    }
  };

  const validateFile = (file) => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ];

    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    if (!allowedTypes.includes(file.type)) {
      return 'File type not supported. Please upload PDF, JPG, PNG, or DOC files';
    }

    return null;
  };

  const handleDrop = async (acceptedFiles) => {
    if (uploading) {
      return;
    }

    if (!acceptedFiles.length) {
      toast.notify({
        type: 'error',
        message: 'No file selected'
      });
      return;
    }

    const validFiles = [];
    for (const file of acceptedFiles) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        toast.notify({
          type: 'error',
          message: `${file.name}: ${validationError}`
        });
        continue;
      }
      validFiles.push(file);
    }

    if (!validFiles.length) {
      return;
    }

    setError(null);
    setUploadingFileName(validFiles.length === 1 ? validFiles[0].name : `uploading ${validFiles.length} file(s)`);
    setUploadProgress(0);

    try {
      // Pass single file or array of files to onUpload, along with progress callback
      await onUpload(validFiles.length === 1 ? validFiles[0] : validFiles, (progress) => {
        setUploadProgress(progress);
      });
      toast.notify({
        type: 'success',
        message:
          validFiles.length === 1
            ? 'Document uploaded successfully'
            : `${validFiles.length} documents uploaded successfully`
      });
      setUploadProgress(0);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to upload document(s)';
      setError(errorMessage);
      toast.notify({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setUploadingFileName('');
      setUploadProgress(0);
    }
  };

  const handleDelete = async (file) => {
    if (deletingFileUrl || uploading) {
      return;
    }

    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    setDeletingFileUrl(file.documentUrl);
    try {
      // Pass the file object which contains documentUrl (the S3 path)
      await onDelete(file);
      toast.notify({
        type: 'success',
        message: 'Document deleted successfully'
      });
    } catch (err) {
      const errorMessage = err?.message || 'Failed to delete document';
      toast.notify({
        type: 'error',
        message: errorMessage
      });
    } finally {
      setDeletingFileUrl(null);
    }
  };

  return (
    <BaseDialog open={open} onClose={onClose} size="md" classes={{ paper: classes.container }}>
      <Label type="greenBig" className={classes.modalHeader}>
        Submit documents for {getTaskDisplayName(taskName)} task
      </Label>
      <Label type="darkGreenSmall" className={classes.modalContent}>
        Please upload all required documents for this task. See the task
        {appcuesModalId ? (
          <span onClick={handleLearnMore} className={classes.link}>
            details and requirements
          </span>
        ) : (
          <>details and requirements</>
        )}
        for more information.
      </Label>

      <div className={classes.content}>
        <Card className={classes.documentsCard}>
          {hasExistingFiles && (
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>FILE NAME</TableCell>
                  <TableCell className={classes.headerCell}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {existingFiles.map((file, index) => {
                  const fileName = file.fileName || file.documentUrl?.split('/').pop() || `Document ${index + 1}`;
                  const isDeleting = deletingFileUrl === file.documentUrl;
                  return (
                    <TableRow key={index} className={classes.tableRow}>
                      <TableCell className={classes.fileNameCell}>
                        <div className={classes.fileNameContainer}>
                          <span className={classes.fileName}>{fileName}</span>
                        </div>
                      </TableCell>
                      <TableCell className={classes.actionsCell}>
                        <div className={classes.actions}>
                          {onDownload && (
                            <IconButton
                              aria-label="view"
                              onClick={() => onDownload(file.documentUrl)}
                              className={classes.actionButton}
                              size="small"
                              disabled={deletingFileUrl !== null}
                            >
                              <img src={openFileIcon} alt="open file" />
                            </IconButton>
                          )}
                          {onDelete && (
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleDelete(file)}
                              className={classes.deleteButton}
                              size="small"
                              disabled={deletingFileUrl !== null || uploading}
                            >
                              {isDeleting ? (
                                <CircularProgress size={20} className={classes.deleteSpinner} />
                              ) : (
                                <img src={deleteIcon} alt="delete" />
                              )}
                            </IconButton>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
          {!hasExistingFiles && (
            <Label type="italicLight" className={classes.noDocsMessage}>
              You haven't uploaded any documents for this task yet
            </Label>
          )}
          <div>
            <DropzoneContainer
              onUpload={handleDrop}
              error={error}
              deleting={false}
              uploading={uploadingFileName || uploading}
              progress={uploadProgress}
              classes={classes}
            />

            {!error && !uploading && (
              <Grid container direction="column" alignItems="center" spacing={1} className={classes.supportText}>
                <Grid item>
                  <Typography variant="body2" className={classes.supportedFormats}>
                    Supported formats: PDF, JPG, PNG, DOC, DOCX (Max 10MB)
                  </Typography>
                </Grid>
              </Grid>
            )}
          </div>
        </Card>
      </div>

      <div className={classes.modalActions}>
        <Button
          variant="contained"
          color="secondary"
          onClick={onClose}
          disabled={uploading || deletingFileUrl !== null}
        >
          Done
        </Button>
      </div>
    </BaseDialog>
  );
};

TaskDocumentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  taskName: PropTypes.string.isRequired,
  onUpload: PropTypes.func.isRequired,
  uploading: PropTypes.bool,
  existingFiles: PropTypes.array,
  onDownload: PropTypes.func,
  onDelete: PropTypes.func
};

TaskDocumentModal.defaultProps = {
  uploading: false,
  existingFiles: [],
  onDownload: null,
  onDelete: null
};

export default observer(TaskDocumentModal);
